import React, { useEffect, useState } from "react";
import PaymentOptionsContainer from "./PaymentOptionsContainer";
import Upi from "./Upi/Upi";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton } from "antd";
import Wallet from "./wallet/Wallet";
import { generateDynamicPaymentMethodLogo, getPaymentMethodIconColors, getStorage, handleRoutingOnPaymentStatus, isEmpty } from "../../../Utils/utils";
import Bnpl from "./bnpl/bnpl";
import { selectIsMobileLayout } from "../../../slices/MobileLayoutSlice";
import PaymentOptionsMobileContainer from "./PaymentOptionsMobileContainer";
import CardPaymentOption from "./cardPayment/cardPaymentOption";
import CardMobilePayment from "./cardPayment/cardMobilePayment/cardMobilePayment";
import {
  getAllPaymentMethods,
  selectBaseColor,
  selectDarkColor,
  selectLightColor,
  selectSelectionColor,
} from "../../../slices/PaymentSessionDetailsSlice";
import NetBanking from "./netBanking/NetBanking";
import QrisQr from "./Qris/QrisQr";
import PixQr from "./Pix/PixQr";
import PaynowQr from "./Paynow/PaynowQr";
import Recommended from "./recommended/Recommended";
import Emi from "./emi/emi";
import { categorySelectedEvent } from "../../../Utils/uiAnalytics"; // Ensure correct import
import { getTransactionStatus, selectTransactionData } from "../../../slices/TransactionIdSlice";
import { useRef } from "react";
import { resetPaymentInitiation, showFailedModal } from "../../../slices/PaymentInitiationSlice";
import { resetUpiStatus, toggleUpiVpaTimer } from "../../../slices/UpiSlice";
import NativeOtp from "./nativeOtp/NativeOtp";

const PaymentOptions = () => {
  const dispatch = useDispatch();
  const [selectedTabType, setSelectedTabType] = useState(null);
  const darkColor = useSelector((state) => selectDarkColor(state));
  const lightColor = useSelector((state) => selectLightColor(state));
  const baseColor = useSelector((state) => selectBaseColor(state));
  const selectionColor = useSelector((state) => selectSelectionColor(state));
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const paymentMethodDictionary = useSelector((state) => state.paymentSessionDetails.paymentMethodDictionary);
  const isMobileLayout = useSelector((state) => selectIsMobileLayout(state));
  const latestSavedAddress = useSelector((state) => state.paymentSessionDetails.latestSavedAddress);
  const totalPaymentSummaryValue = useSelector((state) => state.additionalComponents.totalPaymentSummaryValue);
  const browserCountry = useSelector((state) => state.paymentSessionDetails.browserCountry);
  const appliedOfferDetail = useSelector(state => state.commonPersisted.appliedOfferDetail)
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const paymentInitiationData = useSelector((state) => state.paymentInitiation.paymentInitiationData);
  const transactionId = getStorage("session", "t_id") || getStorage("session", "s_d")?.lastTransactionId;
  const intervalRef = useRef(null);
  const hasFetchedPaymentMethods = useRef(false);
  const selectedActivePaymentTab = useSelector(state => state?.offers?.selectedActivePaymentTab)
  const paymentSuccessData = useSelector((state) => selectTransactionData(state));

  const selectedTabTypes = ["Upi", "UpiOneTimeMandate", "Paynow"]
  const { iconStroke, iconFill } = getPaymentMethodIconColors(darkColor, lightColor);

  const tabList = [
    ...(paymentMethodDictionary?.Recommended?.length > 0
      ? [{
        id: 0,
        type: "Recommended",
        logo: generateDynamicPaymentMethodLogo(
          "Recommended",
          iconStroke,
          iconFill,
          "18px",
          "18px"
        ),
        activeLogo: generateDynamicPaymentMethodLogo(
          "Recommended",
          "#FFFFFF",
          "#D9D9D9",
          "18px",
          "18px"
        ),
        content: (
          <Recommended
            recommendedMethodInfo={paymentMethodDictionary.Recommended}
          />
        ),
      }]
      : []),

    {
      id: 10,
      type: "Paynow",
      logo: paymentMethodDictionary?.Paynow?.[0]?.logoUrl ? (
        <img src={paymentMethodDictionary.Paynow[0].logoUrl} width="18px" height="18px" alt="PayNow" />
      ) : null,
      activeLogo: paymentMethodDictionary?.Paynow?.[0]?.logoUrl ? (
        <img src={paymentMethodDictionary.Paynow[0].logoUrl} width="18px" height="18px" alt="PayNow" />
      ) : null,
      content: (
        <PaynowQr paynowData={paymentMethodDictionary?.Paynow?.[0]} />
      ),
    },

    {
      id: 1,
      type: "Upi",
      logo: generateDynamicPaymentMethodLogo(
        "Upi",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "Upi",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <Upi variant="upi" upiMethodInfo={paymentMethodDictionary?.Upi} />
      ),
    },

    {
      id: 7,
      type: "UpiOneTimeMandate",
      logo: generateDynamicPaymentMethodLogo(
        "Upi",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "Upi",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <Upi variant="mandate" upiMethodInfo={paymentMethodDictionary?.UpiOneTimeMandate} />
      ),
    },


    {
      id: 2,
      type: "Card",
      logo: generateDynamicPaymentMethodLogo(
        "Card",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "Card",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <CardPaymentOption
          cardMethodInfo={paymentMethodDictionary?.Card}
        />
      ),
      mobileContent: (
        <CardMobilePayment
          cardMethodInfo={paymentMethodDictionary?.Card}
        />
      ),
    },

    {
      id: 3,
      type: "Wallet",
      logo: generateDynamicPaymentMethodLogo(
        "Wallet",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "Wallet",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <Wallet
          walletMethodInfo={paymentMethodDictionary?.Wallet}
          darkColor={darkColor}
          lightColor={lightColor}
        />
      ),
    },

    {
      id: 4,
      type: "NetBanking",
      logo: generateDynamicPaymentMethodLogo(
        "NetBanking",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "NetBanking",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <NetBanking
          netBankingMethodInfo={paymentMethodDictionary?.NetBanking}
          darkColor={darkColor}
          lightColor={lightColor}
        />
      ),
    },

    {
      id: 5,
      type: "Emi",
      logo: generateDynamicPaymentMethodLogo(
        "Emi",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "Emi",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <Emi emiMethodInfo={paymentMethodDictionary?.Emi} />
      ),
    },

    {
      id: 6,
      type: "BuyNowPayLater",
      logo: generateDynamicPaymentMethodLogo(
        "BuyNowPayLater",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "BuyNowPayLater",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <Bnpl
          walletMethodInfo={paymentMethodDictionary?.BuyNowPayLater}
          darkColor={darkColor}
          lightColor={lightColor}
        />
      ),
    },
    {
      id: 8,
      type: "QRIS",
      logo: generateDynamicPaymentMethodLogo(
        "QRIS",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "QRIS",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <QrisQr qrisData={paymentMethodDictionary?.QRIS?.[0]} />
      ),
    },
    {
      id: 9,
      type: "PIX",
      logo: generateDynamicPaymentMethodLogo(
        "PIX",
        iconStroke,
        iconFill,
        "18px",
        "18px"
      ),
      activeLogo: generateDynamicPaymentMethodLogo(
        "PIX",
        "#FFFFFF",
        "#D9D9D9",
        "18px",
        "18px"
      ),
      content: (
        <PixQr pixData={paymentMethodDictionary?.PIX?.[0]} />
      ),
    },

  ];


  const handleSelectedTabId = (item) => {
    // item is the tab's array index in tabList (as passed by PaymentOptionsContainer),
    // not the tab's own `id` field - those are two different numbering schemes.
    const selectedTab = tabList[item];
    if (selectedTab) {
      setSelectedTabType(selectedTab.type);
    }
  };

  useEffect(() => {
    const findActiveTab = tabList[selectedActivePaymentTab]
    const paymentDataType = paymentInitiationData?.paymentMethod?.type
    const paymentDataBrand = paymentInitiationData?.paymentMethod?.brand
    if (!isEmpty(findActiveTab) && (selectedTabTypes.includes(findActiveTab?.type))) {
      if (paymentInitiationData && selectedTabTypes.includes(paymentDataType)) {
        let token = sessionDetails?.token;
        const actionType = paymentInitiationData?.actions[0]?.type;
        //this checks if the upi collect brand is used to start the upi vpa timer
        if ((paymentDataBrand === "UpiCollect" || paymentDataBrand === "UpiCollectOtm") && actionType !== 'html') {
          dispatch(toggleUpiVpaTimer(true));
        }
        intervalRef.current = setInterval(() => {
          dispatch(getTransactionStatus({ token, transactionId }));
        }, 4000);
        return () => {
          clearInterval(intervalRef.current);
        };
      }
    }
  }, [paymentInitiationData, selectedActivePaymentTab]);

  useEffect(() => {
    if (selectedTabType) {
      categorySelectedEvent(selectedTabType);
    }
  }, [selectedTabType]);


  const stopStatusCall = () => {
    clearInterval(intervalRef.current);
    if (paymentInitiationData && (paymentInitiationData?.paymentMethod?.brand === "UpiCollect" || paymentInitiationData?.paymentMethod?.brand === "UpiCollectOtm")) {
      dispatch(toggleUpiVpaTimer(false));
      dispatch(resetPaymentInitiation());
      dispatch(resetUpiStatus());
    }
  };

  useEffect(() => {
    if (!paymentSuccessData) return

    handleRoutingOnPaymentStatus({
      paymentSuccessData,
      sessionDetails,
      stopStatusCall,
      dispatch,
      showFailedModal
    });
  }, [paymentSuccessData]);

  useEffect(() => {
    if (isEmpty(paymentMethodDictionary) && !hasFetchedPaymentMethods.current) {
      hasFetchedPaymentMethods.current = true;
      dispatch(
        getAllPaymentMethods({
          token: sessionDetails?.token,
          countryCode: latestSavedAddress?.countryCode,
          browserTimeZone,
          ...(totalPaymentSummaryValue && { amount: totalPaymentSummaryValue }),
          ...(appliedOfferDetail?.codes?.length && { offerId: appliedOfferDetail.codes[0] })
        })
      );
    }
  }, [paymentMethodDictionary])

  const desktopOnlyTabTypes = ["QRIS", "PIX"];
  const mobileTabList = tabList.filter((tab) => !desktopOnlyTabTypes.includes(tab.type));

  const hasNativeOtpAction =
    paymentInitiationData?.paymentMethod?.type === "Card" &&
    paymentInitiationData?.actions?.some((action) => action?.type === "nativeOtp");

  return paymentMethodDictionary ? (
    <div style={{ position: "relative" }}>
      {isMobileLayout ? (
        <PaymentOptionsMobileContainer
          tabList={mobileTabList}
          darkColor={baseColor}
          lightColor={selectionColor}
        />
      ) : (
        <PaymentOptionsContainer
          tabList={tabList}
          darkColor={baseColor}
          lightColor={selectionColor}
          handleSelectedTabId={handleSelectedTabId}
        />
      )}
      {hasNativeOtpAction && <NativeOtp />}
    </div>
  ) : (
    <Skeleton active large />
  );
};

export default PaymentOptions;